/**
 * CS Audit Edit Modal
 *
 * Full edit modal for CyberSweep CS Audit records.
 * Uses Object Manager page layouts for dynamic section-based rendering.
 * Follows the Regal Main pattern of edit modals for consistency.
 */

import React, { useState, useEffect } from 'react';
import { X, ShieldCheck } from 'lucide-react';
import { StandardButton } from '../standard';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';

interface CSAuditData {
  id: string;
  name?: string;
  clientId: string;
  clientName: string;
  auditor?: string;
  assignedAnalyst?: string;
  dueDate: string;
  fiscalYear: string;
  auditType: string;
  auditPhase?: string;
  phase1DueDate: string;
  phase2DueDate: string;
  phaseStatus: string;
  status?: string;
  roadBlockNote?: string;
  roadblockNotes?: string;
  websiteCount?: number;
  completionDate?: string | null;
  createdBy?: string;
  createdDate?: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
  lastModified?: string;
}

interface CSAuditEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  auditData: CSAuditData;
  onSave: (data: Partial<CSAuditData>) => void;
}

// Read-only fields that should not be editable
const READ_ONLY_FIELDS = ['name', 'clientId', 'createdDate', 'lastModified', 'websiteCount'];

export const CSAuditEditModal: React.FC<CSAuditEditModalProps> = ({
  isOpen,
  onClose,
  auditData,
  onSave
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch page layout from Object Manager (dynamic sections)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('CSAudit__c', auditData);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && auditData) {
      // Map auditData fields to form data, supporting multiple field name conventions
      setFormData({
        name: auditData.name || auditData.id,
        clientId: auditData.clientId,
        clientName: auditData.clientName,
        assignedAnalyst: auditData.assignedAnalyst || auditData.auditor,
        dueDate: auditData.dueDate,
        fiscalYear: auditData.fiscalYear,
        auditType: auditData.auditType,
        auditPhase: auditData.auditPhase || 'Phase 1',
        phaseStatus: auditData.phaseStatus,
        status: auditData.status || 'In Progress',
        phase1DueDate: auditData.phase1DueDate,
        phase2DueDate: auditData.phase2DueDate,
        completionDate: auditData.completionDate || '',
        roadblockNotes: auditData.roadblockNotes || auditData.roadBlockNote || '',
        websiteCount: auditData.websiteCount || 0
      });
      setErrors({});
    }
  }, [isOpen, auditData]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    // Clear error when user changes field
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call

      // Map form data back to expected format, including legacy field names
      const saveData = {
        ...formData,
        auditor: formData.assignedAnalyst, // Legacy field mapping
        roadBlockNote: formData.roadblockNotes // Legacy field mapping
      };

      onSave(saveData);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-[#1e293b] rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Edit CS Audit
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {auditData.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content - Dynamic Sections from Page Layout */}
        <div className="flex-1 overflow-y-auto p-6">
          {loadingLayout ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
            <div className="space-y-6">
              {/* Render sections dynamically from Object Manager */}
              {pageLayout.sections
                .filter(section => section.id !== 'section_cs_audit_system') // Skip system info section
                .map(section => (
                  <EditSection
                    key={section.id}
                    section={section}
                    objectApiName="CSAudit__c"
                    formData={formData}
                    onChange={handleFieldChange}
                    errors={errors}
                    disabled={isSaving}
                  />
                ))}

              {/* System Information - Always show at bottom */}
              <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                  System Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Created By:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {auditData.createdBy || 'System'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Created Date:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {auditData.createdDate || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Last Modified By:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {auditData.lastModifiedBy || 'N/A'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Last Modified:</span>
                    <span className="ml-2 text-gray-900 dark:text-gray-100">
                      {auditData.lastModifiedDate || auditData.lastModified || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>No page layout configured for CS Audit.</p>
              <p className="text-sm mt-2">
                Go to Object Manager to configure page layouts.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-end gap-3">
            <StandardButton
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </StandardButton>
            <StandardButton
              variant="primary"
              onClick={handleSubmit}
              disabled={isSaving || loadingLayout}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </StandardButton>
          </div>
        </div>
      </div>
    </div>
  );
};
